import {
  STANDARD_PROHIBITED_ITEMS,
  SKIP_PROHIBITED_ITEMS,
} from "lib/constants/prohibitedItems";
import { CatalogueItem } from "lib/types";
import { getInitialValues } from "containers/ProductDetails/ProductDetails.validation";

export const getProhibitedItems = (
  isSkipJob: boolean,
): Record<string, readonly string[]> => {
  return isSkipJob ? SKIP_PROHIBITED_ITEMS : STANDARD_PROHIBITED_ITEMS;
};

export interface RedirectItem {
  service: keyof typeof redirectItems;
  keywords: string[];
}

interface DetectionItem {
  service: (categorySku: string) => boolean;
  message: string;
  keywords: string[];
}

export const redirectItems = {
  SKIPS: ["soil"],
} as const;

const detectionMapping: DetectionItem[] = [
  {
    service: (categorySku) => categorySku === "MANVAN",
    message: "hwa.components.tooltip.heavyWeight",
    keywords: [
      "concrete",
      "soil",
      "slab",
      "slabs",
      "rubble",
      "brick",
      "bricks",
    ],
  },
];

export const getExtrasTooltip = (
  value: string,
  extras: ReturnType<typeof getInitialValues>["extras"],
  surcharges: CatalogueItem[],
): CatalogueItem["sku"][] => {
  const tooltipItems: CatalogueItem["sku"][] = [];

  for (const { title, sku } of surcharges) {
    if (extras[sku]) {
      continue;
    }

    const regex = `\\b(${title
      .replace(/[\W_]+/g, " ")
      .split(" ")
      .reduce(
        (regex, word, index) => `${regex}${!index ? "" : "|"}${word}(s|es)?`,
        "",
      )})\\b`;
    const match = value.match(new RegExp(regex, "gi"));

    if (match?.length) {
      tooltipItems.push(sku);
    }
  }

  return tooltipItems;
};

export const getProhibitedItemsTooltip = (
  value: string,
  prohibitedItems: Record<string, readonly string[]>,
): string[] => {
  const tooltipItems: string[] = [];

  for (const [item, keywords] of Object.entries(prohibitedItems)) {
    const regex = `\\b(${keywords.join("|")})\\b`;
    const match = value.match(new RegExp(regex, "gi"));

    if (match?.length) {
      tooltipItems.push(item);
    }
  }

  return tooltipItems;
};

export const getRedirectItemsTooltip = (
  value: string,
  service: CatalogueItem["sku"],
): RedirectItem[] => {
  const items: RedirectItem[] = [];

  for (const [item, keywords] of Object.entries(redirectItems)) {
    if (service === item) {
      continue;
    }

    const filterKeywords = keywords.filter(
      (keyword) => value.match(new RegExp(`\\b${keyword}\\b`, "gi"))?.length,
    );

    if (!filterKeywords.length) {
      continue;
    }

    items.push({
      service: item,
      keywords: filterKeywords,
    } as RedirectItem);
  }

  return items;
};

export const getDetectedMessages = (
  value: string,
  categorySku: CatalogueItem["sku"],
): string[] => {
  const items: string[] = [];

  for (const { service, keywords, message } of detectionMapping) {
    if (!service(categorySku)) {
      continue;
    }

    const filterKeywords = keywords.filter(
      (keyword) => value.match(new RegExp(`\\b${keyword}\\b`, "gi"))?.length,
    );

    if (!filterKeywords.length) {
      continue;
    }

    items.push(message);
  }

  return items;
};

export const mergeList = (list: string[]): string => {
  return list.reduce(
    (total, keyword, index) =>
      `${total}${
        index ? (list.length - 1 === index ? " & " : ", ") : ""
      }${keyword}`,
    "",
  );
};
